import { yupResolver } from '@hookform/resolvers/yup';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Box,
  IconButton,
  Button,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { authContext, Position } from '@staffedupusa/shared-components';
import { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import AssistantIcon from '@mui/icons-material/Assistant';

import {
  checkBoxOptions,
  jobPostingStatuses,
  payFrequencyTypes,
  SubmitJobPosting,
  updatedPositionTypes,
} from '@features/jobPostings';
import { useAddJobPosting } from '@features/jobPostings/hooks/useAddJobPosting';
import { useUpdateJobPosting } from '@features/jobPostings/hooks/useUpdateJobPosting';
import {
  emojiWarningMessage,
  PositionOption,
  useGetPositions,
} from '@features/positions';
import RequestPositionModal from '@features/positions/components/RequestPositionModal/RequestPositionModal';

import { track } from '@amplitude/analytics-browser';
import { getUserRegionAndIP } from '@utils/amplitudeUserProperties';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import BundledEditor from '@utils/BundledEditor';
import { Close, HelpOutline } from '@mui/icons-material';
import { selectedPlanName } from '@utils/pricing';
import { containsEmoji, getStrippedText } from '@utils/helper';
import NoCompensationWarningModal from '../NoCompensationWarningModal/NoCompensationWarningModal';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import { AITooltipText } from '@features/messaging';
import JobClosedSuggessionModal from '../JobClosedSuggessionModal/JobClosedSuggessionModal';

type JobPostingModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleNext?: () => void;
  jobPosting: Position | null;
  standardPositionId?: number | null;
};

const schema: yup.ObjectSchema<SubmitJobPosting> = yup
  .object({
    id: yup.number(),
   standard_position_id: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? null : Number(originalValue),
      )
      .nullable()
      .required('Position Category is required'),
    s_title: yup
      .string()
      .required('Title is required')
      .test('not-empty-html', 'Title cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    status: yup.number().nullable().required('Position Status is required'),
    s_description: yup
      .string()
      .required('Description is required')
      .test('not-empty-html', 'Description cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    s_type: yup.string().required('Position Type is required'),
    s_pay_frequency: yup.string().required('Pay Frequency is required'),
    job_location: yup.number().required('Job Location is required'),
    s_salary: yup.number().nullable(),
    s_salary_max: yup.number().nullable(),
    current_staff_count: yup.string().required('Current staff is required'),
    desired_staff_count: yup.string().required('Full staff par is required'),
    tips: yup.boolean(),
    commission: yup.boolean(),
    bonus: yup.boolean(),
    incentives: yup.boolean(),
    benefits: yup.boolean(),
  })
  .required();

const JobPostingModal: FC<JobPostingModalProps> = ({
  isOpen,
  handleClose,
  handleNext,
  jobPosting,
  standardPositionId,
}) => {
  const { authData } = useContext(authContext);
  const industryId = authData.user?.company?.industries[0]?.id ?? 0;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);

  const [errorText, setErrorText] = useState<string>('');
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const [noCompensionWarning, setNoCompensionWarning] = useState(false);
  const [isJobLoading, setIsJobLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<PositionOption[]>([]);
  const [isRequestPositionModalOpen, setIsRequestPositionModalOpen] =
    useState(false);
  const [showClosedSuggesionModal, setShowClosedSuggesionModal] = useState<
    number | null
  >(null);
  const [isPayRange, setIsPayRange] = useState<boolean>(
    Boolean(jobPosting?.s_salary_max),
  );
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SubmitJobPosting>({
    resolver: yupResolver(schema),
    defaultValues: {
      standard_position_id: null,
      s_title: '',
      current_staff_count: 0,
      desired_staff_count: 0,
      status: null,
      s_description: '',
      job_location: 0,
      s_type: '',
      s_pay_frequency: '',
      s_salary: null,
      s_salary_max: null,
      tips: false,
      commission: false,
      bonus: false,
      incentives: false,
      benefits: false,
    },
  });

  const descriptionContainsEmoji = containsEmoji(watch('s_description') || '');
  const { addJobPosting, isAddingJobPosting } = useAddJobPosting({
    setErrorText,
  });
  const { updateJobPosting, isUpdatingJobPosting } = useUpdateJobPosting({
    refetch: true,
    setErrorText,
  });

  useGetPositions({
    setPositions: setOptions,
    industryId,
  });
  const handleInsertAIContent = (content: string) => {
    setValue('s_description', content);
    setShowAiPromptModal(false);
  };

  const handleSubmitForm = async (data: SubmitJobPosting) => {
    if (!data.s_salary) {
      setNoCompensionWarning(true);
      return;
    }
    if (data.status === 1 && jobPosting) {
      setShowClosedSuggesionModal(Number(jobPosting.id));
      return;
    }
    onSubmit(data);
  };
  const onSubmit = async (values: SubmitJobPosting) => {
    !standardPositionId &&
      track(
        `${amplitudeEventsName.JOBMANAGEMENT_CLICK_SAVE_JOB}-${selectedPlan}`,
      );
    setIsJobLoading(true);
    const result = await getUserRegionAndIP();
    if (isPayRange && !values.s_salary) {
      setErrorText('Please enter a minimum salary');
      setIsJobLoading(false);
      return;
    }
    if (isPayRange && !values.s_salary_max) {
      setErrorText('Please enter a maximum salary');
      setIsJobLoading(false);
      return;
    }
    if (values.s_salary && values.s_salary_max) {
      if (Number(values.s_salary) > Number(values.s_salary_max)) {
        setErrorText('Minimum salary cannot be greater than maximum salary');
        setIsJobLoading(false);
        return;
      }
    }
    const payload = {
      ...values,
      ...(handleNext ? { status: 1, indeed_id: values.status } : {}),
      ...(values.s_salary === null && { s_salary: '' }),
      ...(values.s_salary_max === null && { s_salary_max: '' }),
    };

    const amplitudeEvent = {
      eventType: amplitudeEventsName.ADDED_POSITION_ONBOARDING,
      eventProperties: {
        ...(authData.user && { userId: authData.user?.id }),
        ...(result && result?.ip && { 'ip-address': result.ip }),
        ...(result && result?.region && { region: result.region }),
        ...(authData.user && { email: authData.user?.email }),
        'position-name': watch('s_title'),
        description: 'The user added a position during the onboarding',
        'position-category': options?.find(
          (item) => item.id == watch('standard_position_id'),
        )?.name,
        'position-type': watch('s_type'),
        'position-status':
          watch('status') == 1
            ? 'Closed'
            : watch('status') == 2
              ? 'Accepting'
              : 'Hiring',
      },
    };

    const requestOptions = {
      onSuccess: (data: { message: string; status: number }) => {
        if (data.status === 200) {
          setIsJobLoading(false);
          standardPositionId &&
            track(amplitudeEvent.eventType, amplitudeEvent.eventProperties);
          handleNext && handleNext();
          setIsPayRange(false);
          handleClose();
          reset();
          toast.success('Job Posting Saved');
        } else {
          setErrorText(
            data.message ||
            'There was an error saving the job posting. Please try again.',
          );
          setIsJobLoading(false);
        }
      },
      onError: () => {
        setIsJobLoading(false);
        setErrorText(
          'There was an error saving the job posting. Please try again.',
        );
      },
    };
    return jobPosting
      ? updateJobPosting(
        { ...payload, id: jobPosting.id } as SubmitJobPosting,
        requestOptions,
      )
      : addJobPosting(payload as SubmitJobPosting, requestOptions);
  };

  useEffect(() => {
    if (jobPosting) {
      setValue('standard_position_id', jobPosting.standard_position.id);
      setValue('s_title', jobPosting.s_title);
      setValue('s_description', jobPosting.s_description);
      setValue(
        'status',
        handleNext ? Number(jobPosting.indeed_id) : jobPosting.status,
      );
      setValue(
        's_type',
        jobPosting.s_type && jobPosting.s_type === 'Singe Event'
          ? 'Single Event'
          : jobPosting.s_type,
      );
      if (jobPosting.s_salary) {
        setValue('s_salary', Number(jobPosting.s_salary));
      }
      if (jobPosting.s_pay_frequency) {
        setValue('s_pay_frequency', jobPosting.s_pay_frequency);
      } else {
        setValue('s_pay_frequency', '');
      }
      if (jobPosting.s_salary_max) {
        setValue('s_salary_max', Number(jobPosting.s_salary_max));
        setIsPayRange(true);
      }
      if (jobPosting.job_location) {
        setValue('job_location', jobPosting.job_location);
      } else {
        setValue('job_location', 0);
      }
      setValue('tips', jobPosting.tips);
      setValue('current_staff_count', jobPosting.staff_count.current_staff_count);
      setValue('desired_staff_count', jobPosting.staff_count.desired_staff_count);
      setValue('commission', jobPosting.commission);
      setValue('bonus', jobPosting?.bonus ?? false);
      setValue('incentives', jobPosting?.incentives ?? false);
      setValue('benefits', jobPosting?.benefits ?? false);
    } else if (!standardPositionId) {
      reset();
      setValue('status', 3);
      setValue('s_type', 'Full time');
    }
    if (!jobPosting) {
      setValue('s_pay_frequency', 'Hourly');
    }
  }, [jobPosting]);

  useEffect(() => {
    if (standardPositionId) {
      const selectedSP = options.find((item) => item.id === standardPositionId);
      setValue('standard_position_id', standardPositionId);
      setValue('status', 3);
      setValue('s_type', 'Hourly');
      setValue('s_title', selectedSP?.name ?? '');
      setValue('s_description', selectedSP?.description ?? '');
      setValue('s_salary', null);
    }
  }, [options?.length, standardPositionId]);

  useEffect(() => {
    if (!isPayRange) {
      setValue('s_salary_max', null);
    }
  }, [isPayRange]);



  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          handleClose();
          setIsPayRange(false);
          setErrorText('');
          jobPosting && handleNext && handleNext();
        }}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={() => {
              handleClose();
              setIsPayRange(false);
              setErrorText('');
              jobPosting && handleNext && handleNext();
            }}
          >
            <Close />
          </IconButton>
          <br />
          <Typography variant="h4" sx={{ pt: standardPositionId ? 1 : 3 }}>
            {standardPositionId
              ? 'Customize and save your job description'
              : jobPosting
                ? 'Edit a Position'
                : 'Add a Position'}
          </Typography>
          {standardPositionId && (
            <Typography mt={1}>
              Adding pay-rates will get your job ranked higher on external job
              boards!
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack style={{ marginBottom: 10 }} spacing={3}>
            {errorText && <Alert severity="error">{errorText}</Alert>}
            {descriptionContainsEmoji && (
              <Alert severity="warning">{emojiWarningMessage}</Alert>
            )}
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <Stack
                spacing={3}
                sx={{
                  pt: 1,
                  '& p': {
                    ml: 1,
                    fontSize: '15px',
                  },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Controller
                    control={control}
                    name="standard_position_id"
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        value={
                          options.find(
                            (option) =>
                              option.id === watch('standard_position_id'),
                          ) || value
                        }
                        options={options}
                        inputValue={autocompleteInputValue}
                        sx={{ width: '100%' }}
                        onInputChange={(e, newValue) => {
                          if (newValue === "I can't find the position I need") {
                            setAutocompleteInputValue('');
                          } else {
                            setAutocompleteInputValue(newValue);
                          }
                        }}
                        onChange={(e, data: PositionOption | null) => {
                          if (data?.id === 0) {
                            return setIsRequestPositionModalOpen(true);
                          }

                          if (data) {
                            setValue('s_title', data.name, {
                              shouldValidate: true,
                              shouldTouch: true,
                              shouldDirty: true,
                            });
                            setValue('s_description', data.description ?? '');
                          }
                          onChange(data?.id ?? null);
                        }}
                        filterOptions={(opts: PositionOption[], inputState) =>
                          opts.filter(
                            (option) =>
                              option.id === 0 ||
                              option.name
                                .toLowerCase()
                                .includes(inputState.inputValue.toLowerCase()),
                          )
                        }
                        getOptionLabel={(option) =>
                          typeof option === 'object' &&
                            option !== null &&
                            'name' in option
                            ? option.name
                            : ''
                        }
                        renderInput={(params) => (
                          <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            sx={{ width: '100%' }}
                          >
                            <TextField
                              {...params}
                              label="Position Category"
                              variant="outlined"
                              size="small"
                              fullWidth
                              error={Boolean(errors.standard_position_id)}
                              helperText={
                                errors?.standard_position_id
                                  ? errors?.standard_position_id.message
                                  : ''
                              }
                            />
                            <Tooltip
                              title="Please select a position category first. You can name your position below."
                              placement="right"
                              arrow
                            >
                              <HelpOutlineIcon />
                            </Tooltip>
                          </Stack>
                        )}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="s_title"
                    render={({ field }) => (
                      <Stack
                        spacing={2}
                        direction="row"
                        alignItems="center"
                        sx={{ width: '100%' }}
                      >
                        <TextField
                          {...field}
                          value={field.value || ''}
                          label="Position Title"
                          hiddenLabel
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={Boolean(errors.s_title)}
                          helperText={
                            errors?.s_title ? errors?.s_title.message : ''
                          }
                        />
                        <Tooltip
                          title="Customize your position names to reflect the jobs you offer, your position categories will help them get found in the search no matter what you name them."
                          placement="right"
                          arrow
                        >
                          <HelpOutlineIcon />
                        </Tooltip>
                      </Stack>
                    )}
                  />
                </Stack>
                <Stack
                  spacing={3}
                  direction={{ xs: 'column', sm: 'row' }}
                  sx={{ width: '100%' }}
                >
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Controller
                      name="desired_staff_count"
                      defaultValue={0}
                      control={control}
                      rules={{
                        required: 'This field is required',
                        pattern: {
                          value: /^\d+$/,
                          message: 'Only digits are allowed',
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Fully staffed par"
                          type="text"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                          }}
                          onKeyDown={(e) => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            const cleaned = target.value.replace(/\D/g, '');
                            target.value = cleaned;
                            field.onChange(cleaned);
                          }}
                        />
                      )}
                    />

                    <Tooltip
                      placement="right"
                      arrow
                      title="The amount of team members in this position you need to be fully staffed."
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Controller
                      name="current_staff_count"
                      control={control}
                      defaultValue={0}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Current staff level"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                          }}
                          onKeyDown={(e) => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            const cleaned = target.value.replace(/\D/g, '');
                            target.value = cleaned;
                            field.onChange(cleaned);
                          }}
                        />
                      )}
                    />
                    <Tooltip
                      placement="right"
                      arrow
                      title="The amount of team members in this position you currently have on staff."
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </Box>
                </Stack>


                <Controller
                  control={control}
                  name="s_description"
                  render={({ field }) => (
                    <Stack spacing={1}>
                      <BundledEditor
                        {...field}
                        onEditorChange={(newValue: string) =>
                          field.onChange(newValue)
                        }
                        onChange={() => { }}
                        init={{
                          height: 200,
                          menubar: false,
                          plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                          toolbar:
                            'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                          placeholder: 'Description (optional)',
                          spellchecker_dialog: true,
                          spellchecker_rpc_url: 'localhost/ephox-spelling',
                          spellchecker_language: 'en_US',
                          spellchecker_active: true,
                          browser_spellcheck: true,
                          contextmenu: false,
                        }}
                      />
                      {Boolean(errors?.s_description?.message) && (
                        <Typography color="error">
                          {errors?.s_description?.message}
                        </Typography>
                      )}

                    </Stack>
                  )}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>

                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.status)} sx={{ width: { xs: '100%', sm: '70%' } }}>
                        <InputLabel id="position-status-label" size="small">
                          Position Status
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="position-status-label"
                          label="Position Status"
                          size="small"
                        >
                          {jobPostingStatuses.map(({ status, label }) => (
                            <MenuItem key={status} value={status}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors?.status && (
                          <FormHelperText error>
                            {errors?.status.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Stack sx={{ width: { xs: '100%', sm: '30%' } }} mt={1} spacing={2} direction="row" alignItems="center" justifyContent='end'>
                    <Button
                      sx={{ width: '100%' }}
                      variant="outlined"
                      size='small'
                      onClick={() => setShowAiPromptModal(true)}
                      startIcon={<AssistantIcon />}
                    >
                      Write with AI
                    </Button>
                    <Tooltip title={AITooltipText} placement="top" arrow>
                      <HelpOutline color="secondary" fontSize="small" />
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>

                  <Controller
                    control={control}
                    name="s_type"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.s_type)} fullWidth>
                        <InputLabel id="position-type-label" size="small">
                          Position Type
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="position-type-label"
                          label="Position Type"
                          size="small"
                        >
                          {updatedPositionTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors?.s_type && (
                          <FormHelperText error>
                            {errors?.s_type.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={control}
                    name="job_location"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.job_location)} fullWidth>
                        <InputLabel id="job_location-type-label" size="small">
                          Job Location Type
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="job_location"
                          label="Job Location Type"
                          size="small"
                        >

                          <MenuItem value={0}>
                            In person
                          </MenuItem>
                          <MenuItem value={1}>
                            Remote
                          </MenuItem>
                        </Select>
                        {errors?.s_type && (
                          <FormHelperText>
                            {errors?.s_type.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Stack>

                <Stack
                  spacing={{ xs: 0, sm: 3 }}
                  direction={{ xs: 'column', sm: 'row' }}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Tooltip
                      title="Adding compensation is mandatory for many of the job boards including Zip and Indeed, and will help your jobs get seen more! Use a single rate or range if your pay rate is negotiable based on experience."
                      placement="right"
                      arrow
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </Tooltip>
                    <Typography>Compensation</Typography>
                  </Stack>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={isPayRange} />}
                    label="Use a range"
                    onChange={() => setIsPayRange(!isPayRange)}
                    sx={{ marginLeft: '0 !important', whiteSpace: 'nowrap' }}
                  />

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems="center"
                  >
                    <Controller
                      control={control}
                      name="s_salary"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          type="text"
                          size="small"
                          fullWidth
                          sx={{ maxWidth: '120px' }}
                          placeholder="0.00"
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9.]/g, '');
                            const parts = cleaned.split('.');
                            const limited =
                              parts.length > 2
                                ? parts[0] + '.' + parts.slice(1).join('')
                                : cleaned;
                            const maxLength = limited.includes('.') ? 7 : 6;
                            if (limited.length <= maxLength) {
                              field.onChange(limited || null);
                            }
                          }}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              'Backspace',
                              'Tab',
                              'ArrowLeft',
                              'ArrowRight',
                              'Delete',
                              '.',
                            ];
                            if (
                              !/^\d$/.test(e.key) &&
                              !allowedKeys.includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                            if (
                              e.key === '.' &&
                              String(field.value ?? '').includes('.')
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasted = e.clipboardData.getData('Text');
                            if (!/^\d*\.?\d*$/.test(pasted)) {
                              e.preventDefault();
                            }
                          }}
                          InputProps={{
                            inputMode: 'decimal',
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                    {isPayRange && (
                      <>
                        <Typography>-</Typography>
                        <Controller
                          control={control}
                          name="s_salary_max"
                          render={({ field }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              type="text"
                              size="small"
                              fullWidth
                              sx={{ maxWidth: '120px' }}
                              placeholder="0.00"
                              onChange={(e) => {
                                const raw = e.target.value;
                                const cleaned = raw.replace(/[^0-9.]/g, '');
                                const parts = cleaned.split('.');
                                const limited =
                                  parts.length > 2
                                    ? parts[0] + '.' + parts.slice(1).join('')
                                    : cleaned;
                                const maxLength = limited.includes('.') ? 7 : 6;
                                if (limited.length <= maxLength) {
                                  field.onChange(limited || null);
                                }
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = [
                                  'Backspace',
                                  'Tab',
                                  'ArrowLeft',
                                  'ArrowRight',
                                  'Delete',
                                  '.',
                                ];
                                if (
                                  !/^\d$/.test(e.key) &&
                                  !allowedKeys.includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                                if (
                                  e.key === '.' &&
                                  String(field.value ?? '').includes('.')
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                const pasted = e.clipboardData.getData('Text');
                                if (!/^\d*\.?\d*$/.test(pasted)) {
                                  e.preventDefault();
                                }
                              }}
                              InputProps={{
                                inputMode: 'decimal',
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachMoneyIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </>
                    )}
                    <Controller
                      control={control}
                      name="s_pay_frequency"
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '144px' }}
                          error={Boolean(errors.s_type)}
                          fullWidth
                        >
                          <InputLabel id="pay-frequency-label" size="small">
                            Pay Frequency
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="pay-frequency-label"
                            label="Pay Frequency"
                            size="small"
                          >
                            {payFrequencyTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors?.s_pay_frequency && (
                            <FormHelperText error>
                              {errors?.s_pay_frequency.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Stack>
                </Stack>
                <Grid container justifyContent="space-between">
                  {checkBoxOptions.map((option) => (
                    <Grid item xs={6} sm="auto">
                      <Controller
                        key={option.name}
                        control={control}
                        name={option.name}
                        render={({ field: { value, onChange } }) => (
                          <FormControlLabel
                            sx={{ width: '100%' }}
                            control={
                              <Checkbox
                                checked={value}
                                onChange={(e) => onChange(e.target.checked)}
                              />
                            }
                            label={option.label}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>

                <LoadingButton
                  type="submit"
                  startIcon={<DoneIcon />}
                  variant="contained"
                  size="small"
                  fullWidth
                  loading={isJobLoading}
                  loadingPosition="start"
                >
                  {standardPositionId ? 'Save and continue' : 'Save'}
                </LoadingButton>
              </Stack>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>
      <RequestPositionModal
        isOpen={isRequestPositionModalOpen}
        handleClose={() => setIsRequestPositionModalOpen(false)}
      />
      {noCompensionWarning && (
        <NoCompensationWarningModal
          open={noCompensionWarning}
          handleEdit={() => {
            setNoCompensionWarning(false);
          }}
          handleSave={() => {
            setNoCompensionWarning(false);
            onSubmit(watch());
          }}
        />
      )}
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
      {Boolean(showClosedSuggesionModal) && (
        <JobClosedSuggessionModal
          open={Boolean(showClosedSuggesionModal)}
          type={0}
          onProactivelyRecruit={() => {
            if (showClosedSuggesionModal) {
              onSubmit({
                ...watch(),
                status: 2,
              })
              setShowClosedSuggesionModal(null);
            }
          }}
          onCloseMyJob={() => {
            if (showClosedSuggesionModal) {
              onSubmit({
                ...watch(),
                status: 1,
              })
              setShowClosedSuggesionModal(null);
            }
          }}
          onCloseDialog={() => setShowClosedSuggesionModal(null)}
        />
      )}
    </>
  );
};

export default JobPostingModal;
